import { PricingEntry } from './PricingEntry';
import { createMoney, getDisplayCurrency } from 'shared/models/money';
import { AdditionalCost, ObjectPricing } from '@/domain';
import { getPricePerNight } from '@/utils/price';
import { usePriceData } from '@/hooks/usePriceData';
import { useUnleash } from '@/hooks/unleash';
import { useAdditionalCost } from '@/hooks/useAdditionalCosts';
import { DEFAULT_CURRENCY } from '@/constants';

export interface PricingProps {
    adId: string;
    pricing: ObjectPricing;
    isPrivate: boolean;
}

export const Pricing = ({ adId, pricing, isPrivate }: PricingProps) => {
    const currency = pricing.currency || DEFAULT_CURRENCY;
    const { data } = usePriceData(adId);
    const { data: additionalCost } = useAdditionalCost(adId);
    const unleash = useUnleash();
    const toggleTJVR = unleash?.tjvrRelease ?? false;

    const pricePerNightFrom = data?.defaultPriceDay ? data?.defaultPriceDay : getPricePerNight(pricing);
    const depositValue = getDepositValue(pricing);
    const minRequiredNight = pricing?.minimumRentalDuration ?? 1;

    return (
        <div className="grid md:grid-cols-2 gap-16" data-testid="adPricing">
            <div className="grid gap-16" data-testid="entry-col-1">
                {pricePerNightFrom && (
                    <PricingEntry
                        description={'Pris per natt'}
                        value={`fra ${getDisplayCurrency(createMoney({ value: pricePerNightFrom, currency }))}`}
                        additionalInfo={'Prisen er oppgitt uten rabatter og ekstra kostnader'}
                    />
                )}
                {minRequiredNight > 1 && minRequiredNight < 6 && (
                    <PricingEntry description={'Minste opphold'} value={`${minRequiredNight} netter`} />
                )}
                {minRequiredNight == 6 && <PricingEntry description={'Minste opphold'} value={'1 uke'} />}
                {minRequiredNight == 13 && <PricingEntry description={'Minste opphold'} value={'2 uker'} />}
                {!toggleTJVR && pricing.year && <PricingEntry description={'Åremål/langtidsleie'} value={'Kontakt utleier'} />}
            </div>
            <div className="grid gap-16" data-testid="entry-col-2">
                {!toggleTJVR && pricing.currency && <PricingEntry description={'Pris oppgitt i'} value={pricing.currency} />}
                {!toggleTJVR && depositValue && <PricingEntry description={'Depositum'} value={depositValue} />}
                {isPrivate
                    ? additionalCost &&
                      updatedOrValidOldAdditionalCosts(additionalCost) && (
                          <PricingEntry description={'Rengjøring'} value={getCleaningValue(additionalCost, pricing?.currency || 'NOK')} />
                      )
                    : pricing.cleaning && (
                          <PricingEntry
                              description={'Rengjøring'}
                              value={getDisplayCurrency(createMoney({ value: pricing.cleaning, currency }))}
                          />
                      )}
                {!toggleTJVR && pricing.linens && (
                    <PricingEntry
                        description={'Håndklær og sengetøy'}
                        value={getDisplayCurrency(createMoney({ value: pricing.linens, currency }))}
                    />
                )}
            </div>
        </div>
    );
};

const updatedOrValidOldAdditionalCosts = (additionalCost: AdditionalCost): boolean =>
    additionalCost.priceCleaningType !== 'NOT_YET_UPDATED' || additionalCost.priceCleaning !== null;

const getCleaningValue = (additionalCost: AdditionalCost, currency: string): string => {
    if (
        (additionalCost?.priceCleaningType === 'FEE' || additionalCost?.priceCleaningType === 'NOT_YET_UPDATED') &&
        additionalCost?.priceCleaning
    ) {
        const money = createMoney({ value: additionalCost.priceCleaning, currency: currency || DEFAULT_CURRENCY });
        return getDisplayCurrency(money);
    }
    if (additionalCost?.priceCleaningType === 'GUEST_CLEANS') {
        return 'Leietaker vasker';
    }
    return 'Inkludert i leieprisen';
};

const getDepositValue = (pricing: ObjectPricing): string | null => {
    if (!pricing || typeof pricing.depositAmount !== 'number' || pricing.depositType === 'NONE') {
        return null;
    }

    const depositAmountTotal = createMoney({ value: pricing.depositAmount, currency: pricing.currency || DEFAULT_CURRENCY });

    switch (pricing.depositType) {
        case 'FIXED':
            return getDisplayCurrency(depositAmountTotal);
        case 'PERCENT':
            return `${depositAmountTotal.value}%`;
        default:
            return 'Ja';
    }
};
